import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Star, Loader2, MessageCircle, Users, ArrowLeft, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/public/AuthModal";
import Conversation from "@/components/games/Conversation";
import { Stars } from "@/components/games/GameCards";
import { TagList } from "@/components/games/gameMeta";
import GameMedia from "@/components/games/GameMedia";
import { ShareGame, EditGameInfo } from "@/components/games/GameInfoActions";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const RATE_CLS = "inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-yellow-400 text-black font-bold text-sm shadow-[0_8px_30px_-10px_rgba(250,204,21,0.7)] hover:bg-yellow-300 hover:-translate-y-0.5 transition-[transform,background-color]";

function RateButton({ g, onRated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [auth, setAuth] = useState(false);
  const [hover, setHover] = useState(0);
  const rate = async (score) => {
    try { const r = await api.post(`/gamehub/item/${g.id}/rate`, { score }); onRated(r.data); setOpen(false); toast.success(`You rated ${score}/10`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not rate"); }
  };
  if (g.is_owner) return <span className="text-xs text-white/40" data-testid="rate-own-game">Your game — players rate it</span>;
  const label = g.my_rating
    ? <><Star size={16} className="fill-black" /> <span data-testid="my-rating">Your rating: {g.my_rating}/10</span> <span className="font-semibold opacity-70">· Change</span></>
    : <><Star size={16} /> Rate this game</>;
  if (!user) return <><button onClick={() => setAuth(true)} data-testid="rate-btn" className={RATE_CLS}>{label}</button><AuthModal open={auth} onClose={() => setAuth(false)} /></>;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><button data-testid="rate-btn" className={RATE_CLS}>{label}</button></PopoverTrigger>
      <PopoverContent align="end" className="w-auto bg-[#0d1117] border-white/10 p-4" data-testid="rate-popover">
        <div className="text-xs text-white/50 mb-2 text-center">Rate {g.name}</div>
        <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <button key={n} onMouseEnter={() => setHover(n)} onClick={() => rate(n)} data-testid={`rate-${n}`} className="flex flex-col items-center gap-0.5 p-0.5">
              <Star size={22} className={(hover || g.my_rating || 0) >= n ? "text-yellow-400 fill-yellow-400" : "text-white/25"} />
              <span className="text-[10px] text-white/40">{n}</span>
            </button>
          ))}
        </div>
        <div className="mt-2 text-center text-sm font-bold text-yellow-400 h-5">{hover ? `${hover}/10` : ""}</div>
      </PopoverContent>
    </Popover>
  );
}

function Stat({ label, children }) {
  return <div className="text-center"><div className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1">{label}</div>{children}</div>;
}

function Tile({ icon: Icon, label, href, testid }) {
  return (
    <a href={href} data-testid={testid} className="card-surface rounded-xl grid place-items-center py-5 md:py-6 text-center hover:bg-white/5 transition-colors">
      <div><Icon className="mx-auto text-white/70" size={22} /><div className="mt-2 text-[11px] md:text-xs font-bold uppercase tracking-wider text-white/70">{label}</div></div>
    </a>
  );
}

export default function GameDetail() {
  const { id } = useParams();
  const [g, setG] = useState(null);
  const [err, setErr] = useState(false);
  const [comments, setComments] = useState(null);
  const { user } = useAuth();

  useEffect(() => { api.get(`/gamehub/item/${id}`).then((r) => setG(r.data)).catch(() => setErr(true)); }, [id, user?.user_id]);

  if (err) return <div className="py-24 text-center text-white/50" data-testid="game-not-found">Game not found. <Link to="/games" className="text-[hsl(var(--primary))]">Back to games</Link></div>;
  if (!g) return <div className="py-24 grid place-items-center text-white/30"><Loader2 className="animate-spin" /></div>;
  const canEdit = g.is_owner || user?.is_admin;
  const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6 pt-6 md:pt-8 pb-16" data-testid="game-detail">
      <Link to="/games" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white mb-4"><ArrowLeft size={13} /> All games</Link>
      {g.status === "pending" && <div className="mb-4 px-4 py-2.5 rounded-xl bg-amber-500/10 text-amber-400 text-sm flex items-center gap-2" data-testid="game-pending-banner"><Clock size={14} /> Waiting for admin approval — only you can see this page.</div>}

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight break-words" data-testid="game-title">{g.name}</h1>
          <div className="mt-1 text-sm text-white/50">{[g.publisher, g.published_at && new Date(g.published_at).getFullYear(), (g.platforms || []).join(" · ")].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 shrink-0 py-3 md:py-0 border-y border-white/10 md:border-0 justify-between md:justify-end">
          <Stat label="7L Rating">
            <div className="flex items-center gap-1.5"><Star size={22} className="text-yellow-400 fill-yellow-400" /><span className="font-head text-2xl font-bold text-white" data-testid="game-rating">{g.rating_count ? g.rating_avg.toFixed(1) : "–"}</span><span className="text-sm text-white/40">/10</span></div>
            <div className="text-[11px] text-white/40" data-testid="game-rating-count">{g.rating_count} {g.rating_count === 1 ? "rating" : "ratings"}</div>
          </Stat>
          <div className="self-center flex items-center gap-2"><RateButton g={g} onRated={(d) => setG({ ...g, ...d })} />{g.status !== "pending" && <ShareGame g={g} />}</div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-[minmax(0,240px)_1fr_150px] gap-3">
        <img src={mediaUrl(g.img)} alt={g.name} className="hidden md:block w-full aspect-[2/3] object-cover rounded-xl" />
        <div className="relative rounded-xl overflow-hidden aspect-video md:aspect-auto bg-black">
          <img src={mediaUrl(g.screenshot || g.img)} alt={g.name} className="absolute inset-0 w-full h-full object-cover" data-testid="game-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-4 left-4"><Stars value={g.rating_avg} size={20} /></div>
        </div>
        <div className="grid gap-3 grid-cols-2 md:grid-cols-1">
          <Tile icon={MessageCircle} label={`${comments ?? g.comment_count} ${(comments ?? g.comment_count) === 1 ? "Comment" : "Comments"}`} href="#conversation" testid="comments-tile" />
          <Tile icon={Users} label={`${g.rating_count} ${g.rating_count === 1 ? "Rating" : "Ratings"}`} href="#top" testid="ratings-tile" />
        </div>
      </div>

      <div className="mt-6 max-w-3xl">
        <TagList g={g} />
        <p className="mt-4 text-base text-white/85 leading-relaxed whitespace-pre-line" data-testid="game-description">{g.description || "No description yet."}</p>
        <dl className="mt-5 divide-y divide-white/10 border-y border-white/10 text-sm">
          <div className="flex gap-6 py-3"><dt className="w-28 shrink-0 font-bold text-white">Publisher</dt><dd className="text-[hsl(var(--primary))]" data-testid="game-publisher">{g.publisher || "—"}</dd></div>
          <div className="flex gap-6 py-3"><dt className="w-28 shrink-0 font-bold text-white">Platform</dt><dd className="text-white/70" data-testid="game-platforms">{(g.platforms || []).join(", ") || "—"}</dd></div>
          <div className="flex gap-6 py-3"><dt className="w-28 shrink-0 font-bold text-white">Published</dt><dd className="text-white/60" data-testid="game-published">{g.published_at ? fmtDate(g.published_at) : "—"}</dd></div>
        </dl>
        {canEdit && <div className="mt-3"><EditGameInfo g={g} onSaved={(d) => setG({ ...g, ...d })} /></div>}
      </div>

      <div id="game-media" className="scroll-mt-24"><GameMedia g={g} canEdit={canEdit} onSaved={(d) => setG({ ...g, ...d })} /></div>

      <div id="conversation" className="scroll-mt-24 mt-12 card-surface rounded-2xl p-4 sm:p-6 md:p-8">
        {g.status !== "pending" ? <Conversation gameId={g.id} onCount={setComments} /> : <p className="text-sm text-white/40">The conversation opens once this game is published.</p>}
      </div>
    </div>
  );
}
